import React, { useCallback, useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { trackEvent } from '../../analytics';
import { formatBytes } from '../../videoUpload/videoCaps';
import { ORIGIN_UPLOAD, ORIGIN_PHOTO_COMPILE } from '../../utils/videoOrigin';
import {
  combinePreconditions, describeMismatchedProperties, manifestDiff,
} from '../../utils/footageConcat';
import { footageCountLabel } from '../../lib/operationCopy';

// "Combine footage" CTA — and, once a combined video exists, the re-combine
// affordance for it (multi-video Phase 3). The direct peer of PhotoCompileCta,
// deliberately shaped the same way: the server writes project.videoConcat
// { status, progress, phase, manifest, error? } from the callable and the
// worker, so every downstream state renders off THAT field, never a local flag
// — a refresh mid-combine has to show the combine.
//
// Five shapes, decided in this order:
//   1. a combine is in flight → progress card. First because it is the only
//      state that is still moving, and because it renders whether or not a
//      combined video already exists (that is the re-combine case, and hiding
//      it would make a re-combine look like nothing happened).
//   2. the last combine failed → error card + Retry, carrying the worker's own
//      message. Above the blocked/offer states for the same reason: a failure
//      the user has not seen yet outranks an offer to start another one.
//   3. the video slot holds something a combine must not replace → an
//      explanatory notice, not an offer. BOTH layers enforce this: the server
//      refuses with FAILED_PRECONDITION / VIDEO_ALREADY_EXISTS (the truth,
//      and the only thing standing between the user and an orphaned upload),
//      and canCombineFootage() mirrors the rule here so the user gets the
//      EXPLANATION without having to fire a doomed request. Do not delete the
//      client check on the grounds that the server has one — it is what turns
//      a bare toast into a sentence about the user's own footage.
//      A server refusal that reaches us anyway means our snapshot was stale
//      (a video landed in the slot between render and click); that answer is
//      promoted into this same shape rather than shown as a toast — see
//      `serverBlock`.
//   4. the footage has changed since the combine → the re-combine CTA, naming
//      what changed.
//   5. nothing combined yet → the first-combine CTA.
//
// FREE. Combining charges no credits (locked pricing decision) — credits are
// charged at RENDER, on the combined duration, exactly as for an uploaded
// single video. There is deliberately no price row and no credits path here.

// Statuses in which the callable REFUSES a second combine. Kept in lock-step
// with _IN_FLIGHT_CONCAT_STATUSES in functions/timelapse/api/main.py.
const IN_FLIGHT_STATUSES = ['queued', 'processing'];

// Human labels for the worker-written videoConcat.phase (video_concat.py).
// Unknown/absent phases render as the bare percent, so an in-flight doc written
// by an older worker (or one mid-deploy) degrades to a plain percentage rather
// than "undefined".
const PHASE_LABELS = {
  preparing: 'Preparing clips',
  combining: 'Combining clips',
  finalizing: 'Finishing up',
};

// Single-clip variant: "combining clips" is nonsense when there is only one,
// and there is nothing to prepare relative to — every phase just reads as
// "getting the one clip ready", so 'preparing' and 'combining' collapse to
// the same word. Derived from PHASE_LABELS so a phase added to the worker
// later can't silently lose its label on one-clip runs; 'finalizing' is
// unchanged.
const PHASE_LABELS_SINGLE = { ...PHASE_LABELS, preparing: 'Preparing', combining: 'Preparing' };

// Why a combine is refused over an existing source video, in the user's words.
// Same split as PhotoCompileCta's blockedReason: "you uploaded this" is a claim
// about what the user did, and `unknown` is us admitting we can't tell. A
// project whose combined video predates the `origin` marker resolves to unknown
// despite being a real combine output, so asserting "uploaded" there would be
// flatly false. Anything not provably an upload gets the hedge.
function blockedReason(origin) {
  if (origin === ORIGIN_UPLOAD) {
    return 'This project already has a video you uploaded, and combining this footage would take its place.';
  }
  if (origin === ORIGIN_PHOTO_COMPILE) {
    return 'This project’s video was built from photos, and combining this footage would take its place.';
  }
  return 'We can’t confirm this project’s video came from these clips, so combining them would risk replacing footage you may not be able to get back.';
}

function formatClock(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return '0:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const plural = (n, word) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;

// The local half of the server's per-clip preconditions, as one sentence for
// the CTA. The per-CLIP detail goes on the rows (FootageVideoList's `issues`
// prop) — this only says which wall we are at.
function preconditionMessage({ blocking, notReady, incompatible, clipCount }) {
  if (blocking === 'FOOTAGE_NOT_READY') {
    const errored = notReady.filter((issue) => issue.status === 'error');
    if (errored.length > 0) {
      const one = errored.length === 1;
      // "Remove it to combine the rest" needs there to BE a rest. When every
      // clip in the project errored — the single-clip project being the common
      // way to get here — the way out is a clip we can read, not pressing on
      // with a remainder that doesn't exist.
      if (errored.length >= clipCount) {
        return `${plural(errored.length, 'clip')} couldn’t be read. Remove ${one ? 'it' : 'them'} and upload ${one ? 'a clip' : 'clips'} we can read.`;
      }
      return `${plural(errored.length, 'clip')} couldn’t be read. Remove ${one ? 'it' : 'them'} to combine the rest.`;
    }
    return `${plural(notReady.length, 'clip')} still being read — this finishes on its own.`;
  }
  if (blocking === 'FOOTAGE_INCOMPATIBLE') {
    // describeMismatchedProperties owns the field-name → English mapping (and
    // folds width+height into one "resolution"), so this sentence and the
    // per-row badges cannot describe the same mismatch with different words.
    const readable = describeMismatchedProperties(incompatible);
    return `These clips can’t be combined without re-encoding — they differ in ${readable.join(', ')}. Combining needs clips from the same camera at the same settings.`;
  }
  return null;
}

const CombineFootageCta = ({
  projectId,
  // The live footage list (useProjectVideos), in playback order.
  videos,
  // project.videoConcat — the server-written state of the last/current combine.
  videoConcat,
  // False when the project's source-video slot holds something a combine must
  // not replace. From canCombineFootage(), which mirrors the combine_footage
  // callable's own VIDEO_ALREADY_EXISTS gate.
  combinable = true,
  // The classification behind that boolean (resolveVideoOrigin()), used only to
  // word the refusal accurately. Defaults to null so an un-passed origin gets
  // the hedged copy rather than a false "you uploaded this".
  videoOrigin = null,
  // A footage upload is still running, so the clip list is a partial snapshot:
  // combining now would produce a video missing the clips still in flight.
  uploadInProgress = false,
  // Per-clip refusals the SERVER returned (details.issues), handed up so the
  // tab can mark the offending ROWS. The list is above this CTA in the DOM, so
  // it can't come down as a prop — it goes up and back down through FootageTab.
  onIssues = null,
}) => {
  const [busy, setBusy] = useState(false);
  const [invocationError, setInvocationError] = useState(null);
  // The server's VIDEO_ALREADY_EXISTS answer, held as { origin }, or null.
  // Set only when the callable refuses a combine our `combinable` prop said was
  // fine — i.e. our snapshot was stale. It exists so that refusal renders as the
  // SAME explanatory card the client would have shown had it known, instead of
  // an inline error under a button that can now never work.
  const [serverBlock, setServerBlock] = useState(null);

  // Drop the server's answer as soon as the project snapshot that contradicted
  // it moves. Without this the block is sticky: the re-combine case (another
  // tab's combine lands in the slot, so `videoOrigin` flips to 'video-concat'
  // and the combine becomes legal again) would stay wedged in the blocked card
  // for the life of the mount. When the fresher snapshot agrees with the server
  // — the common case — `combinable` arrives false and the prop-driven block
  // takes over seamlessly.
  useEffect(() => {
    setServerBlock(null);
  }, [combinable, videoOrigin]);

  const clipCount = (videos || []).length;
  const { clips, notReady, incompatible, blocking } = combinePreconditions(videos);
  const status = videoConcat?.status || null;
  const inFlight = IN_FLIGHT_STATUSES.includes(status);
  const diff = manifestDiff(videos, videoConcat?.manifest);

  // How many clips THIS run covers — the combine's own count, not the live
  // list, because a clip uploaded after the request is not in the run and the
  // project total would overstate it. A run of one has nothing to "combine": it
  // is getting the single clip ready to play back as the project's video, which
  // is the word the button, the progress card and the failure card all use.
  const runClipCount = typeof videoConcat?.sourceCount === 'number'
    ? videoConcat.sourceCount
    : clipCount;
  const isSingleClipRun = runClipCount === 1;

  // The two guards, resolved into one answer. The server's refusal wins on both
  // the verdict and the wording: it read the project doc at the moment of the
  // click, and our props are by definition a snapshot behind it.
  const blocked = !combinable || !!serverBlock;
  const blockedOrigin = serverBlock ? serverBlock.origin : videoOrigin;

  const totalBytes = clips.reduce((a, clip) => a + clip.sizeBytes, 0);
  const probed = clips.filter((clip) => Number.isFinite(clip.durationSec));
  const totalSeconds = probed.reduce((a, clip) => a + clip.durationSec, 0);

  // Why the combine is unavailable right now, or null when it can be started.
  // Ordered most-immediate-first; only one is ever shown. Every reason clears
  // on its own, which is why the button is DISABLED rather than hidden.
  const unavailableReason = uploadInProgress
    ? 'Available when the upload finishes.'
    : preconditionMessage({ blocking, notReady, incompatible, clipCount });
  const combineDisabled = busy || !!unavailableReason;

  const startCombine = useCallback(async () => {
    if (busy) return;
    setInvocationError(null);
    if (onIssues) onIssues([]);
    setBusy(true);
    trackEvent('footage_combine_requested', {
      project_id: projectId,
      clip_count: clipCount,
      total_bytes: totalBytes,
      is_retry: status === 'failed',
      is_recombine: !!videoConcat?.manifest,
    });
    try {
      const combineFootage = httpsCallable(getFunctions(), 'combine_footage');
      await combineFootage({ projectId });
    } catch (err) {
      // Read the structured reason, never err.message: on a transport failure
      // the Firebase SDK sets .message to the bare error code, so a
      // `message || fallback` test never reaches its fallback.
      const reason = err?.details?.reason;
      const issues = err?.details?.issues;
      if (reason === 'FOOTAGE_NOT_READY' || reason === 'FOOTAGE_INCOMPATIBLE') {
        // Hand the per-clip list up so the ROWS say which clip is at fault.
        // This is the whole point of the server returning `issues` rather than
        // one sentence: the local mirror can be a snapshot behind (a clip that
        // errored between our render and the click), and this is how the user
        // finds out WHICH one.
        if (onIssues) onIssues(Array.isArray(issues) ? issues : []);
        setInvocationError(reason === 'FOOTAGE_NOT_READY'
          ? 'Some clips aren’t ready yet — see the list above.'
          : 'Some clips don’t match the others — see the list above.');
      } else if (reason === 'CONCAT_IN_FLIGHT') {
        // Two tabs, or a click that beat the videoConcat snapshot. Not an
        // error the user has to act on: the combine they asked for is running.
        setInvocationError('This project’s footage is already being combined.');
      } else if (reason === 'NO_FOOTAGE') {
        setInvocationError('There is no footage in this project to combine.');
      } else if (reason === 'MULTI_VIDEO_DISABLED') {
        setInvocationError('Combining footage isn’t available yet.');
      } else if (reason === 'VIDEO_ALREADY_EXISTS') {
        // The video slot filled between our render and this click, so the
        // server refused what canCombineFootage() had cleared. Not temporary,
        // and not retryable: promote it into the blocked card rather than
        // leaving an inline error under a button that can only fail again.
        // details.origin ('upload' | 'photo-compile' | 'unknown') is the
        // server's own classification — better than our stale prop, and the
        // reason this reads as a fact rather than a guess.
        setServerBlock({ origin: err?.details?.origin ?? null });
      } else if (err && err.code === 'functions/failed-precondition') {
        setInvocationError('Combining footage is temporarily unavailable.');
      } else {
        setInvocationError('Could not start combining. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }, [busy, projectId, clipCount, totalBytes, status, videoConcat, onIssues]);

  if (clipCount === 0) return null;

  if (inFlight) {
    const pct = Math.min(100, Math.max(0, videoConcat?.progress || 0));
    const phaseLabel = (isSingleClipRun ? PHASE_LABELS_SINGLE : PHASE_LABELS)[videoConcat?.phase];
    return (
      <div className="pi-compile-card" data-testid="footage-combine-card" data-state={status}>
        <div className="pi-compile-head">
          <h4>
            {isSingleClipRun
              ? 'Preparing your video…'
              : `Combining ${plural(runClipCount, 'clip')} into one video…`}
          </h4>
        </div>
        <div className="pi-progress-track">
          <div className="pi-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="pi-compile-sub">
          {status === 'queued'
            ? 'Queued'
            : `${phaseLabel ? `${phaseLabel} — ` : ''}${Math.floor(pct)}%`}
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div
        className="pi-compile-card pi-compile-card--error"
        data-testid="footage-combine-card"
        data-state="failed"
      >
        {/* Worded off the run that actually failed, the same way the progress
            card above is: a user who pressed "Prepare video" must not be told
            that combining their clipS failed, when there was one clip and the
            word never appeared on the button they pressed. */}
        <div className="pi-compile-head">
          <h4>{isSingleClipRun ? 'Preparing your video failed' : 'Combining failed'}</h4>
        </div>
        <div className="pi-compile-sub">
          {videoConcat?.error
            || (isSingleClipRun
              ? 'Something went wrong preparing your clip as this project’s video.'
              : 'Something went wrong combining your clips into one video.')}
        </div>
        <button
          type="button"
          className="btn btn-teal capsule pi-btn-sm pi-compile-cta-btn"
          onClick={startCombine}
          disabled={combineDisabled || blocked}
        >
          {busy ? 'Starting…' : 'Retry'}
        </button>
        {/* The provenance block outranks the precondition text here: a retry
            that would replace an uploaded video is refused for a different
            reason than a clip that is still probing, and only one can be
            acted on. A retry the SERVER refused lands here too — the failed
            card stays put (the user has an unread failure), the retry goes
            dead, and the same sentence explains why. */}
        {blocked && <div className="pi-compile-sub">{blockedReason(blockedOrigin)}</div>}
        {!blocked && unavailableReason && <div className="pi-compile-sub">{unavailableReason}</div>}
        {invocationError && <div className="pi-compile-error">{invocationError}</div>}
      </div>
    );
  }

  if (blocked) {
    return (
      <div
        className="pi-compile-card"
        data-testid="footage-combine-blocked"
        // Which layer decided, for tests and for a support screenshot: 'client'
        // means we never asked, 'server' means we asked and were refused.
        data-blocked-by={serverBlock ? 'server' : 'client'}
      >
        <div className="pi-compile-head">
          <h4>This footage can’t be combined here</h4>
        </div>
        {/* Only on the server path: the card replaced a button the user just
            clicked, so say what changed underneath them. Without it the click
            reads as "nothing happened". */}
        {serverBlock && (
          <div className="pi-compile-sub">
            This project’s video changed since this page loaded.
          </div>
        )}
        <div className="pi-compile-sub">
          {blockedReason(blockedOrigin)} To combine {clipCount === 1 ? 'this clip' : `these ${clipCount.toLocaleString()} clips`}, upload {clipCount === 1 ? 'it' : 'them'} to a new project.
        </div>
      </div>
    );
  }

  // Combined already, and the footage has not moved since. Nothing to offer —
  // stay out of the way until a clip is added, removed, reordered or replaced.
  const isRecombine = !!videoConcat?.manifest;
  if (isRecombine && !diff.changed) return null;

  // One clip, never combined: there is nothing to combine it WITH. "Combine
  // footage" over "1 clip play back as one video" describes an operation the
  // user cannot see the point of, and the sentence does not even parse. Same
  // callable, same free operation — just the honest words for a run of one, and
  // the same ones the in-flight card already uses ("Preparing your video…").
  // Recombines keep the rebuild wording: a one-clip rebuild is still a rebuild
  // of a video that exists.
  const isSingleClip = !isRecombine && clipCount === 1;

  // Name what changed, the way the photo rebuild CTA does. Most-significant
  // first, and only the parts that actually happened — "3 new clips" is a
  // reason to act; "3 new clips · 0 removed" is noise.
  const changeParts = [];
  if (diff.added.length > 0) changeParts.push(`${plural(diff.added.length, 'new clip')}`);
  if (diff.removed.length > 0) changeParts.push(`${plural(diff.removed.length, 'clip')} removed`);
  if (diff.resized.length > 0) changeParts.push(`${plural(diff.resized.length, 'clip')} re-uploaded`);
  if (diff.reordered) changeParts.push('a new playback order');

  return (
    <div
      className="pi-compile-cta"
      data-testid="footage-combine-cta"
      data-mode={isRecombine ? 'recombine' : 'combine'}
    >
      <div className="pi-compile-cta-header">
        <div className="pi-compile-cta-title">
          {isRecombine ? 'Rebuild combined video' : (isSingleClip ? 'Prepare your video' : 'Combine footage')}
        </div>
        <div className="pi-compile-cta-sub" data-testid="footage-combine-summary">
          {unavailableReason
            || (isRecombine
              ? `${changeParts.join(', ')} since this was combined. Rebuild to include ${diff.removed.length > 0 || diff.reordered ? 'the change' : 'them'}.`
              : (isSingleClip
                ? 'Your clip plays back as this project’s video — no re-encoding, so nothing is re-compressed.'
                : `${plural(clipCount, 'clip')} play back as one video — no re-encoding, so nothing is re-compressed.`))}
        </div>
      </div>

      <div className="pi-header-chips">
        {/* Same helper the footage list's own count chip uses, so the two chips
            — stacked one above the other on this screen — cannot disagree
            about what to call these files. */}
        <span className="pi-header-chip">{footageCountLabel(clipCount)}</span>
        <span className="pi-header-chip">{formatBytes(totalBytes)}</span>
        {/* Only once EVERY clip has been probed: a partial sum reads as a
            shorter shoot than the user actually has. */}
        {probed.length === clipCount && totalSeconds > 0 && (
          <span className="pi-header-chip">{formatClock(totalSeconds)} of footage</span>
        )}
      </div>

      <div className="pi-compile-cta-controls">
        <button
          type="button"
          className="btn btn-teal capsule pi-compile-cta-btn"
          onClick={startCombine}
          disabled={combineDisabled}
        >
          {busy ? 'Starting…' : isRecombine ? 'Rebuild combined video' : isSingleClip ? 'Prepare video' : 'Combine footage'}
        </button>
      </div>

      <p className="pi-fps-hint">
        {isSingleClip
          ? 'Preparing your video is free — you’re only charged when you render an edit.'
          : 'Combining is free — you’re only charged when you render an edit.'}
        {isRecombine
          ? ' The rebuilt video replaces the current one. Edits you have already rendered are kept.'
          : (isSingleClip
            ? ' Your original clip stays in the project.'
            : ' Your original clips stay in the project.')}
      </p>

      {invocationError && <div className="pi-compile-error">{invocationError}</div>}
    </div>
  );
};

export default CombineFootageCta;
